import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import Header from '../components/layout/Header';
import PageWrapper from '../components/layout/PageWrapper';
import { useAppSelector } from '../store/hooks';
import { DASHBOARD } from '../constants/routes';
import { RECRUITER_ROLE } from '../constants/roles';
import Retool from 'react-retool';
import { getRetoolEmbedUrlAnalytics } from '../api/retool';
import Loader from '../UI/Loader';
import NotFound from './NotFound';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import Analytics from '../components/analytics/Analytics';
import Tab from '../UI/Tab';
import { Tabs } from 'antd';

const { TabPane } = Tabs;

const TAB_KEYS = [
  { key: 'overview', label: 'Overview' },
  { key: 'jobs', label: 'Jobs' },
  { key: 'interviews', label: 'Interviews' },
  { key: 'financial', label: 'Financial' },
];

const AnalyticsPage: React.FC<{ pack?: string }> = ({ pack }) => {
  const [t] = useTranslation();
  const { user } = useAppSelector((state) => state.user);
  const history = useHistory();
  const location = useLocation();
  const analyticsModuleEnabled = user?.role === RECRUITER_ROLE
    ? user?.company?.enable_analytics
    : user?.company?.analytics_module_enabled;

  // URL param detection
  const params = new URLSearchParams(location.search);
  const isEmbeddableFromUrl = params.get('embeddable') === 'true';

  // For recruiter users, always use embeddable mode
  const isEmbeddable = user?.role === RECRUITER_ROLE || isEmbeddableFromUrl;

  // Define EMBEDDABLE_IDS within the component to access user and isEmbeddable
  const EMBEDDABLE_IDS: Record<string, string> = useMemo(() => ({
    overview:
      isEmbeddable && user?.role === RECRUITER_ROLE
        ? 'b5a02f96-5cf3-4b19-b8de-cf8d81bc57f9'
        : 'c0531ac4-c05c-436a-bc42-4b58fae121d6', // Dashboard/Overview
    jobs: 'JOBS_EMBEDDABLE_ID',
    interviews: 'INTERVIEWS_EMBEDDABLE_ID',
    financial: 'FINANCIAL_EMBEDDABLE_ID',
  }), [isEmbeddable, user?.role]);

  // Filter tabs based on user role and available embeddable IDs
  const availableTabs = useMemo(() => {
    return user?.role === RECRUITER_ROLE
      ? [{ key: 'overview', label: 'Overview' }] // Only overview for recruiters
      : isEmbeddable
        ? TAB_KEYS.filter(tab => {
          const embeddableId = EMBEDDABLE_IDS[tab.key];
          return embeddableId && !embeddableId.includes('_EMBEDDABLE_ID');
        })
        : TAB_KEYS;
  }, [user?.role, isEmbeddable, EMBEDDABLE_IDS]);

  // Embeddable state
  const [activeTab, setActiveTab] = useState<string>('overview');

  // Retool state
  const loader = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [embedUrl, setEmbedUrl] = useState('');
  const { permissionError, guardedApiCall } = usePermissionGuard();

  // Ensure active tab is valid for filtered tabs
  useEffect(() => {
    if (isEmbeddable) {
      const validTabKeys = availableTabs.map(tab => tab.key);
      if (!validTabKeys.includes(activeTab)) {
        setActiveTab(validTabKeys[0] || 'overview');
      }
    }
  }, [isEmbeddable, availableTabs, activeTab]);

  useEffect(() => {
    if (!analyticsModuleEnabled) {
      history.push(DASHBOARD);
    }
  }, [analyticsModuleEnabled, history]);

  React.useEffect(() => {
    console.log('AnalyticsPage render', { user, isEmbeddable, analyticsModuleEnabled });
  });

  // Only fetch Retool embed URL if not embeddable and not already fetched
  useEffect(() => {
    if (isEmbeddable || embedUrl) return;
    console.log('Fetching Retool embed URL', { user, isEmbeddable });
    if (user?.role === 'client') {
      guardedApiCall(() => getRetoolEmbedUrlAnalytics())
        .then((response) => {
          setEmbedUrl(response.data.embed_url);
          setLoading(false);
          console.log('Retool embed url fetched successfully', response.data.embed_url);
        })
        .catch((err) => {
          setLoading(false);
          console.error('Error fetching Retool embed url', err);
        });
    } else {
      getRetoolEmbedUrlAnalytics()
        .then((response) => {
          setEmbedUrl(response.data.embed_url);
          setLoading(false);
          console.log('Retool embed url fetched successfully', response.data.embed_url);
        })
        .catch((err) => {
          setLoading(false);
          console.error('Error fetching Retool embed url', err);
        });
    }
  }, [user, isEmbeddable, guardedApiCall, embedUrl]);

  if (!isEmbeddable && user?.role === 'client' && permissionError) {
    return <NotFound message={t('NO_PERMISSION')} />;
  }

  // Embeddable UI
  if (isEmbeddable) {
    return (
      <PageWrapper>
        <Header title={t('ANALYTICS_TITLE')} />
        {availableTabs.length > 1 && (
          <Tab
            defaultActiveKey={activeTab}
            activeKey={activeTab}
            onChange={setActiveTab}
          >
            {availableTabs.map(({ key, label }) => (
              <TabPane tab={t(label)} key={key} />
            ))}
          </Tab>
        )}
        {EMBEDDABLE_IDS[activeTab] && !EMBEDDABLE_IDS[activeTab].includes('_EMBEDDABLE_ID') ? (
          <Analytics embeddableId={EMBEDDABLE_IDS[activeTab]} />
        ) : (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <p>{t('Dashboard not available for this section')}</p>
          </div>
        )}
      </PageWrapper>
    );
  }

  // Retool UI
  return (
    <PageWrapper>
      <Header title={t('ANALYTICS_TITLE')} />
      <Loader spinning={loading}>
        {embedUrl ? (
          <div style={{ height: 'calc(100vh - 64px - 3rem)' }}>
            <Retool url={embedUrl} height="100%" />
          </div>
        ) : null}
        <div className="loader" ref={loader} />
      </Loader>
    </PageWrapper>
  );
};

export default AnalyticsPage;
